import { COULEURS } from '../theme/charte';
import { styleStatutMission } from '../utils/statutsMission';
import { DocumentClient, ETATS_A_REGLER } from './documents';
import { avancement, dateLivraison, estEnCollecte, estLivree, idMission, Mission, missionSignee } from './missions';

/* Situation d'une mission vue par le client, telle que les maquettes
   l'affichent (Accueil, Missions, fiche) : statut, prochaine étape,
   avancement, livraison et ligne de détail. Tout se déduit des missions
   (location-service) et des documents v2 (admin-service) déjà chargés :
   aucun appel de plus. */

export type Categorie = 'decision' | 'collecte' | 'livree' | 'etude' | 'autre';

export interface Etape {
  texte: string;
  couleur: string;
  /** Action attendue du client : bouton orange dans la liste Missions. */
  action?: boolean;
  lien?: string;
}

export interface Situation {
  categorie: Categorie;
  statut: { libelle: string; couleur: string };
  etape: Etape;
  /** Texte sous la barre d'avancement. */
  avancementTexte: string;
  /** Pourcentage, ou null quand la barre reste vide (devis, étude). */
  pourcentage: number | null;
  couleurBarre: string;
  livraison: string;
  detail: string;
  devisASigner: DocumentClient | null;
  factureARegler: DocumentClient | null;
  lien: string;
}

export const TYPE_LABELS: Record<string, string> = {
  pharmacies:           'Santé',
  marches:              'Commerce',
  infrastructure:       'Infrastructure',
  transport:            'Transport',
  education:            'Éducation',
  eau_energie:          'Énergie',
  autre:                'Autre',
  collecte_poi:         'Collecte de lieux',
  releve_prix:          'Relevé de prix',
  photo_terrain:        'Photo terrain',
  enquete:              'Enquête',
  verification_poi:     'Vérification de lieux',
  signal_reseau:        'Signal réseau',
  donnees_sanitaires:   'Santé',
  donnees_agricoles:    'Agriculture',
  recensement_commerce: 'Commerce',
};

/** « 9 oct. » */
export function jourCourt(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
}

/** « 30 oct. 2026 » */
export function jourComplet(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** Jours écoulés depuis une date (0 au minimum). */
export function joursDepuis(iso?: string | null, maintenant = Date.now()): number | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? null : Math.max(0, Math.floor((maintenant - t) / 86_400_000));
}

const nombre = (n: number) => n.toLocaleString('fr-FR');

export function categorieMission(m: Mission): string {
  const t = (m.types ?? [])[0];
  return t ? TYPE_LABELS[t] ?? t : '';
}

/** Dernier devis émis pour la mission. */
export function dernierDevis(documents: DocumentClient[], questId: string): DocumentClient | null {
  return documents
    .filter(d => d.type === 'DEVIS' && d.questId === questId)
    .sort((a, b) => (b.emisLe || '').localeCompare(a.emisLe || ''))[0] ?? null;
}

/** Facture de la mission à régler, la plus urgente (échéance la plus proche). */
export function factureARegler(documents: DocumentClient[], questId: string): DocumentClient | null {
  return documents
    .filter(d => d.type === 'FACTURE' && d.questId === questId && ETATS_A_REGLER.includes(d.etatPaiement || ''))
    .sort((a, b) => (a.dateEcheance || a.emisLe || '').localeCompare(b.dateEcheance || b.emisLe || ''))[0] ?? null;
}

export function libelleFacture(f: DocumentClient): string {
  return f.natureFacture === 'ACOMPTE' ? 'Acompte' : f.natureFacture === 'SOLDE' ? 'Solde' : 'Facture';
}

export function situationMission(m: Mission, documents: DocumentClient[]): Situation {
  const id = idMission(m);
  const devis = dernierDevis(documents, id);
  const aSigner = devis && !missionSignee(m) ? devis : null;
  const aRegler = factureARegler(documents, id);
  const factures = documents.filter(d => d.type === 'FACTURE' && d.questId === id);
  const livree = estLivree(m);
  const collecte = estEnCollecte(m);
  const etude = m.statut === 'brouillon' || m.statut === 'en_attente_validation';

  const categorie: Categorie = aSigner ? 'decision' : livree ? 'livree' : collecte ? 'collecte' : etude ? 'etude' : 'autre';
  const statut = aSigner ? { libelle: 'Devis émis', couleur: COULEURS.attention } : styleStatutMission(m.statut);

  let etape: Etape;
  if (aSigner) {
    etape = { texte: 'Signer le devis', couleur: COULEURS.action, action: true, lien: `/documents#devis-${aSigner.id}` };
  } else if (aRegler) {
    const echeance = aRegler.dateEcheance ? ` · ${jourCourt(aRegler.dateEcheance)}` : '';
    etape = { texte: `${libelleFacture(aRegler)} à régler${echeance}`, couleur: COULEURS.attention, lien: '/documents' };
  } else if (etude) {
    etape = { texte: 'Tribe prépare le devis', couleur: COULEURS.texteSecondaire };
  } else if (collecte) {
    etape = { texte: 'Collecte en cours', couleur: COULEURS.texteSecondaire };
  } else if (livree) {
    etape = factures.length > 0 && factures.every(f => f.etatPaiement === 'PAYEE')
      ? { texte: 'Facture réglée', couleur: COULEURS.texteSecondaire }
      : { texte: 'Exporter les résultats', couleur: COULEURS.info, lien: `/missions/${id}?onglet=resultats` };
  } else {
    etape = { texte: '—', couleur: COULEURS.texteDiscret };
  }

  const realisees = m.collectesRealisees ?? 0;
  const attendues = m.collectesAttendues ?? 0;
  const pct = avancement(m);
  let avancementTexte: string;
  let pourcentage: number | null = pct;
  if (aSigner) { avancementTexte = 'Démarre à la signature'; pourcentage = null; }
  else if (etude) { avancementTexte = 'Devis sous 48 h'; pourcentage = null; }
  else if (livree) {
    const fraicheur = joursDepuis(dateLivraison(m));
    avancementTexte = `${nombre(realisees)} / ${nombre(attendues)}${fraicheur != null ? ` · fraîcheur ${fraicheur} j` : ''}`;
  } else {
    avancementTexte = `${nombre(realisees)} / ${nombre(attendues)} collectes · ${pct} %`;
  }

  const morceaux: string[] = [];
  const cat = categorieMission(m);
  if (cat) morceaux.push(cat);
  if (livree) morceaux.push(`${nombre(realisees)} collectes`);
  else if (attendues > 0) morceaux.push(`${nombre(attendues)} lieux ciblés`);
  if (m.spotters && m.spotters.mobilises > 0) morceaux.push(`${m.spotters.mobilises} Spotter${m.spotters.mobilises > 1 ? 's' : ''}`);
  if (!livree) {
    if (collecte && m.dateDebut) morceaux.push(`depuis le ${jourCourt(m.dateDebut)}`);
    else if (etude && m.createdAt) morceaux.push(`demandée le ${jourCourt(m.createdAt)}`);
    else if (aSigner && m.createdAt) morceaux.push(`créée le ${jourCourt(m.createdAt)}`);
  }

  const livraison = livree ? jourComplet(dateLivraison(m)) : aSigner || etude ? '—' : jourComplet(m.dateFin);

  return {
    categorie,
    statut,
    etape,
    avancementTexte,
    pourcentage,
    couleurBarre: livree ? COULEURS.succes : COULEURS.info,
    livraison,
    detail: morceaux.join(' · '),
    devisASigner: aSigner,
    factureARegler: aRegler,
    lien: aSigner ? `/documents#devis-${aSigner.id}` : `/missions/${id}`,
  };
}
